"use client";

import { c as _c } from "react/compiler-runtime";
import { useState, useEffect } from "react";
import { signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, onAuthStateChanged, User, GoogleAuthProvider, signInWithPopup, sendEmailVerification, sendPasswordResetEmail, AuthError } from "firebase/auth";
import { doc, getDoc, setDoc, collection, query, getDocs, where, orderBy, limit } from "firebase/firestore";
import { auth, db } from "@/lib/firebase";
import { Loader2, LogOut, Mail, Lock, User as UserIcon, AlertCircle, CheckCircle, Save, PartyPopper, Shield, MapPin, MessageSquare, Smartphone, Watch, Navigation as AssistantNavigation } from "lucide-react";
import { useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { Suspense } from "react";
const ADMIN_UID = "EkIixz8no1Ouv5KPwIrQHw9ksKo1";
const EmojiEventsIcon = t0 => {
  const $ = _c(3);
  const {
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <path d="M19 5h-2V3H7v2H5c-1.1 0-2 .9-2 2v1c0 2.55 1.92 4.63 4.39 4.94.63 1.5 1.98 2.63 3.61 2.96V19H7v2h10v-2h-4v-3.1c1.63-.33 2.98-1.46 3.61-2.96C19.08 12.63 21 10.55 21 8V7c0-1.1-.9-2-2-2zM5 8V7h2v3.82C5.84 10.4 5 9.3 5 8zm14 0c0 1.3-.84 2.4-2 2.82V7h2v1z" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== className) {
    t2 = <svg viewBox="0 0 24 24" className={className} fill="#FFD700">{t1}</svg>;
    $[1] = className;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
};
export function AccountContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const successParam = searchParams.get('success');
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'login' | 'signup' | 'forgot-password'>('login');
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Player Settings State
  const [nickname, setNickname] = useState("");
  const [beep, setBeep] = useState(false);
  const [announcer, setAnnouncer] = useState(false);
  const [scoreboard, setScoreboard] = useState(false);
  const [isPremium, setIsPremium] = useState(false);
  const [premiumType, setPremiumType] = useState("");
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsSuccess, setSettingsSuccess] = useState<string | null>(null);
  const [settingsError, setSettingsError] = useState<string | null>(null);

  // Admin Stats State
  const [adminStats, setAdminStats] = useState({
    inbox: 0,
    suggestions: 0,
    waitlist: 0,
    newLocations: 0
  });
  const fetchAdminStats = async () => {
    try {
      // Fetch Inbox count
      const msgsSnapshot = await getDocs(collection(db, "dbContact"));
      const allDocs = msgsSnapshot.docs.map(doc => doc.data());
      const inboxCount = allDocs.filter(d => d.message && d.message.trim() !== "" && !["ios_waitlist", "watch_waitlist", "tennis_waitlist", "pickleball_waitlist"].includes(d.type) && d.message !== "Request to join iOS Waitlist").length;

      // Fetch Suggestions count
      const feedbackSnapshot = await getDocs(query(collection(db, "dbFeedback"), where("read", "==", false)));
      const suggestionsCount = feedbackSnapshot.size;

      // Fetch Waitlist count
      const waitlistCount = allDocs.filter(d_0 => d_0.type === "pickleball_waitlist").length;

      // Fetch New Locations count (games without locationId within the last 50)
      const gamesQ = query(collection(db, "dbGameResults"), orderBy("timestampStart", "desc"), limit(50));
      const gamesSnapshot = await getDocs(gamesQ);
      const newLocationsCount = gamesSnapshot.docs.filter(doc_0 => !doc_0.data().court?.locationId).length;
      setAdminStats({
        inbox: inboxCount,
        suggestions: suggestionsCount,
        waitlist: waitlistCount,
        newLocations: newLocationsCount
      });
    } catch (err) {
      console.error("Error fetching admin stats:", err);
    }
  };
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, currentUser => {
      setUser(currentUser);
      if (currentUser) {
        if (currentUser.uid === ADMIN_UID) {
          fetchAdminStats();
        }
        // Fetch user data from Firestore
        const userDocRef = doc(db, "dbUser", currentUser.uid);
        getDoc(userDocRef).then(docSnap => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            setNickname(data.userNickName || "");
            setBeep(data.userSettingBeep || false);
            setAnnouncer(data.userSettingAnnouncer || false);
            setScoreboard(data.userSettingTvScoreboard || false);
            setIsPremium(data.isPremium || false);
            setPremiumType(data.premiumType || "");
          }
          setLoading(false);
        }).catch(err_0 => {
          console.error("Error fetching user data:", err_0);
          setLoading(false);
        });
      } else {
        setLoading(false);
        setNickname("");
        setBeep(false);
        setAnnouncer(false);
        setScoreboard(false);
        setIsPremium(false);
        setPremiumType("");
      }
    });
    return () => unsubscribe();
  }, []);
  const clearState = () => {
    setError(null);
    setMessage(null);
    setIsSubmitting(false);
  };
  const getFriendlyErrorMessage = (error_0: AuthError) => {
    switch (error_0.code) {
      case 'auth/popup-closed-by-user':
        return "The login window was closed before completion. Please try again.";
      case 'auth/cancelled-popup-request':
        return "Only one login popup can be open at a time.";
      case 'auth/popup-blocked':
        return "The login popup was blocked by your browser. Please allow popups for this site.";
      case 'auth/network-request-failed':
        return "Network error. Please check your internet connection.";
      case 'auth/invalid-credential':
        return "Invalid email or password.";
      case 'auth/user-not-found':
        return "No account found with this email.";
      case 'auth/wrong-password':
        return "Incorrect password.";
      case 'auth/email-already-in-use':
        return "This email is already registered. Try signing in instead.";
      case 'auth/weak-password':
        return "Password is too weak. Please use at least 6 characters.";
      case 'auth/too-many-requests':
        return "Too many failed attempts. Please try again later or reset your password.";
      default:
        return error_0.message || "An unexpected error occurred. Please try again.";
    }
  };
  const handleGoogleLogin = async () => {
    clearState();
    setIsSubmitting(true);
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      router.push("/scores");
    } catch (err_1: unknown) {
      const e = err_1 as AuthError;
      setError(getFriendlyErrorMessage(e));
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleLogin = async (e_0: React.FormEvent) => {
    e_0.preventDefault();
    clearState();
    setIsSubmitting(true);
    try {
      await signInWithEmailAndPassword(auth, email, password);
      router.push("/scores");
    } catch (err_2: unknown) {
      setError(getFriendlyErrorMessage(err_2 as AuthError));
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleSignup = async (e_1: React.FormEvent) => {
    e_1.preventDefault();
    clearState();
    setIsSubmitting(true);
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await sendEmailVerification(userCredential.user);
      setMessage("Account created! Please check your email to verify your account.");
      router.push("/scores");
    } catch (err_3: unknown) {
      setError(getFriendlyErrorMessage(err_3 as AuthError));
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleResetPassword = async (e_2: React.FormEvent) => {
    e_2.preventDefault();
    clearState();
    setIsSubmitting(true);
    try {
      await sendPasswordResetEmail(auth, email);
      setMessage("Password reset email sent. Check your inbox.");
      setView('login');
    } catch (err_4: unknown) {
      setError(getFriendlyErrorMessage(err_4 as AuthError));
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleLogout = async () => {
    try {
      await signOut(auth);
    } catch (err_5) {
      console.error("Logout failed", err_5);
    }
  };
  const handleSaveSettings = async () => {
    setSettingsError(null);
    setSettingsSuccess(null);
    if (!user) {
      setSettingsError("You must be logged in to save settings.");
      return;
    }

    // Validation Rules
    const trimmedNickname = nickname.trim();
    if (trimmedNickname !== "" && !/^[a-zA-Z0-9_-]*$/.test(trimmedNickname)) {
      setSettingsError("Only letters, numbers, hyphens, and underscores are allowed.");
      return;
    }
    if (trimmedNickname.length > 16) {
      setSettingsError("Nickname cannot be longer than 16 characters.");
      return;
    }
    const normalizedNickname = trimmedNickname.toLowerCase();
    if (normalizedNickname === "mvplayer" || normalizedNickname === "mvplayers") {
      setSettingsError("'MVPlayer' or 'MVPlayers' are reserved names. Please choose another.");
      return;
    }
    setSavingSettings(true);
    try {
      const userDocRef_0 = doc(db, "dbUser", user.uid);
      await setDoc(userDocRef_0, {
        userNickName: trimmedNickname,
        userSettingBeep: beep,
        userSettingAnnouncer: announcer,
        userSettingTvScoreboard: scoreboard
      }, {
        merge: true
      });
      setSettingsSuccess("Settings saved successfully.");
      setNickname(trimmedNickname);
    } catch (err_6) {
      console.error("Error saving settings:", err_6);
      setSettingsError("Failed to save settings. Please try again.");
    } finally {
      setSavingSettings(false);
    }
  };
  if (loading) {
    return <div className="min-h-[50vh] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#B0D18B]" />
      </div>;
  }

  // --- LOGGED IN VIEW ---
  if (user) {
    return <div className="animate-in fade-in duration-500">
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-24">
          
          {successParam === 'true' && <div className="mb-8 p-6 bg-[#FFD700]/10 border border-[#FFD700]/30 rounded-2xl flex items-center gap-4 text-[#FFD700] animate-in zoom-in duration-300">
              <div className="bg-[#FFD700] p-2 rounded-full shadow-[0_0_15px_rgba(255,215,0,0.3)]">
                <EmojiEventsIcon className="h-6 w-6 !fill-black" />
              </div>
              <div>
                <h3 className="font-bold text-lg font-sriracha">Welcome Original Gangster!</h3>
                <p className="text-sm opacity-90">We will ship your Golden Court Brackets as soon as possible.</p>
              </div>
            </div>}

          <div className="text-center mb-16">
            <h1 className="text-4xl sm:text-5xl font-sriracha tracking-tighter text-[#B0D18B] mb-4" style={{
            animation: "fadeSlideUnblur 1s ease-out 0.6s forwards"
          }}>
              Welcome, {nickname || user.displayName || user.email?.split('@')[0]}!
            </h1>
            <p className="text-[#C4C8BA]" style={{
            animation: "fadeSlideUnblur 1s ease-out 0.8s forwards"
          }}>
              You are currently logged in.
            </p>
          </div>
          
          <div className="max-w-3xl mx-auto space-y-8">
            
            {/* Admin Access Card */}
            {user.uid === ADMIN_UID && <div className="bg-[#1A1C16] border border-[#B0D18B]/30 rounded-2xl p-6 relative hover:border-[#B0D18B] transition-colors duration-200 shadow-[0_0_30px_rgba(176,209,139,0.05)]">
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#B0D18B] text-[#1E3702] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                  Admin Access
                </div>
                <div className="flex items-center justify-center mb-6">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1.5 text-[#8E9285]" title="Inbox Messages">
                       <Mail className="h-4 w-4" />
                       <span className="text-sm font-bold text-[#E2E3D8]">{adminStats.inbox}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[#8E9285]" title="Unread Suggestions">
                       <MessageSquare className="h-4 w-4" />
                       <span className="text-sm font-bold text-[#E2E3D8]">{adminStats.suggestions}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[#8E9285]" title="Pickleball Waitlist">
                       <Image src="/drawables/sports_tennis_24dp_1F1F1F_FILL0_wght400_GRAD0_opsz24.svg" alt="Pickleball" width={16} height={16} className="w-4 h-4 opacity-80" />
                       <span className="text-sm font-bold text-[#E2E3D8]">{adminStats.waitlist}</span>
                    </div>
                    <div className="flex items-center gap-1.5 text-[#8E9285]" title="New Locations">
                       <AssistantNavigation className="h-4 w-4" />
                       <span className="text-sm font-bold text-[#E2E3D8]">{adminStats.newLocations}</span>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row justify-center gap-4 mt-2">
                  <Link href="/account/admin" className="w-full sm:w-1/4 flex items-center justify-center gap-2 bg-[#B0D18B]/10 hover:bg-[#B0D18B]/20 text-[#B0D18B] py-3 rounded-lg transition-colors border border-[#B0D18B]/20 font-bold">
                     <Shield className="h-4 w-4" />
                     Admin
                  </Link>
                  <Link href="/account/locations" className="w-full sm:w-1/4 flex items-center justify-center gap-2 bg-[#B0D18B]/10 hover:bg-[#B0D18B]/20 text-[#B0D18B] py-3 rounded-lg transition-colors border border-[#B0D18B]/20 font-bold">
                     <MapPin className="h-4 w-4" />
                     Locations
                  </Link>
                </div>
              </div>}

            {/* Account Details Card */}
            <div className="bg-[#1A1C16] border border-[#8E9285]/20 rounded-2xl p-6 relative hover:border-[#B0D18B] transition-colors duration-200 shadow-[0_0_30px_rgba(176,209,139,0.05)]">
              <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#B0D18B] text-[#1E3702] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                Account Details
              </div>
              <div className="flex items-center justify-end mb-6">
                {!user.emailVerified && <span className="text-amber-400 text-xs bg-amber-400/10 px-2 py-1 rounded border border-amber-400/20">
                    Email not verified
                  </span>}
              </div>
              
              <div className="space-y-4 mb-8">
                 <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-black/20 rounded-lg">
                    <span className="text-[#8E9285] text-sm mb-1 sm:mb-0">Email</span>
                    <span className="text-[#E2E3D8] font-mono">{user.email}</span>
                 </div>
                 <div className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-black/20 rounded-lg">
                    <span className="text-[#8E9285] text-sm mb-1 sm:mb-0">Username</span>
                    <div className="flex items-center gap-2">
                      <span className="text-[#E2E3D8] font-mono">{user.displayName || "Not set"}</span>
                      {isPremium && <EmojiEventsIcon className="h-5 w-5" />}
                    </div>
                 </div>
                 <div className={`flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-black/20 rounded-lg ${isPremium ? 'border border-[#FFD700]/20' : ''}`}>
                    <span className="text-[#8E9285] text-sm mb-1 sm:mb-0">Membership</span>
                    <div className="flex items-center gap-4">
                      <span className={`${isPremium ? 'text-[#FFD700] font-bold' : 'text-[#E2E3D8]'} font-mono`}>
                        {isPremium ? premiumType : premiumType || "Free Plan"}
                      </span>
                      {!isPremium && <Link href="/store" className="text-xs bg-[#B0D18B]/10 hover:bg-[#B0D18B]/20 text-[#B0D18B] px-3 py-1.5 rounded-full border border-[#B0D18B]/30 font-bold transition-colors">
                          Upgrade
                        </Link>}
                    </div>
                 </div>
              </div>

              <div className="flex justify-center">
                <button onClick={handleLogout} className="w-full sm:w-1/4 flex items-center justify-center gap-2 bg-[#2C2E26] hover:bg-[#3C3E36] text-[#E2E3D8] py-3 rounded-lg transition-colors border border-[#8E9285]/20">
                  <LogOut className="h-4 w-4" />
                  Sign Out
                </button>
              </div>
            </div>

            {/* Player Settings Card */}
            <div className="bg-[#1A1C16] border border-[#8E9285]/20 rounded-2xl p-6 relative hover:border-[#B0D18B] transition-colors duration-200 shadow-[0_0_30px_rgba(176,209,139,0.05)]">
               <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#B0D18B] text-[#1E3702] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider">
                  Player Settings
               </div>
               <p className="text-[#C4C8BA] text-sm leading-relaxed mb-6 pt-2">
                  Configure your player profile and global preferences. These settings will be used when you join a match.
               </p>

               <div className="space-y-6">
                  {/* Settings Error/Success Messages */}
                  {settingsError && <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3 text-red-400 text-sm">
                        <AlertCircle className="h-5 w-5 shrink-0" />
                        <p>{settingsError}</p>
                     </div>}
                  {settingsSuccess && <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg flex items-start gap-3 text-green-400 text-sm">
                        <CheckCircle className="h-5 w-5 shrink-0" />
                        <p>{settingsSuccess}</p>
                     </div>}

                  {/* Nickname Field */}
                  <div className="space-y-1">
                     <label className="text-[#8E9285] text-xs uppercase font-semibold tracking-wider ml-1">Player Name</label>
                     <input type="text" value={nickname} onChange={e_3 => {
                  setNickname(e_3.target.value);
                  setSettingsError(null);
                  setSettingsSuccess(null);
                }} className="w-full bg-black/20 border border-[#8E9285]/20 rounded-lg py-3 px-4 text-[#E2E3D8] placeholder-[#5C5F56] focus:outline-none focus:border-[#B0D18B] transition-colors" placeholder="Enter nickname" />
                     <p className="text-[#5C5F56] text-xs ml-1">Max 16 chars. Letters, numbers, hyphens, and underscores only.</p>
                  </div>

                  {/* Beep Switch */}
                  <div className="flex items-center justify-between p-3 bg-black/20 rounded-lg border border-[#8E9285]/10">
                     <div className="flex flex-col">
                        <span className="text-[#E2E3D8] font-medium">Beep</span>
                        <span className="text-[#8E9285] text-xs">{beep ? "Enabled" : "Disabled"}</span>
                     </div>
                     <button onClick={() => setBeep(!beep)} className={`w-12 h-6 rounded-full transition-colors duration-200 ease-in-out relative ${beep ? 'bg-[#B0D18B]' : 'bg-[#2C2E26]'}`}>
                        <span className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full shadow-sm transition-transform duration-200 ease-in-out ${beep ? 'translate-x-6' : 'translate-x-0'}`} />
                     </button>
                  </div>

                  {/* Announcer Switch */}
                  <div className="flex items-center justify-between p-3 bg-black/20 rounded-lg border border-[#8E9285]/10">
                     <div className="flex flex-col">
                        <span className="text-[#E2E3D8] font-medium">Announcer</span>
                        <span className="text-[#8E9285] text-xs">{announcer ? "Enabled" : "Disabled"}</span>
                     </div>
                     <button onClick={() => setAnnouncer(!announcer)} className={`w-12 h-6 rounded-full transition-colors duration-200 ease-in-out relative ${announcer ? 'bg-[#B0D18B]' : 'bg-[#2C2E26]'}`}>
                        <span className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full shadow-sm transition-transform duration-200 ease-in-out ${announcer ? 'translate-x-6' : 'translate-x-0'}`} />
                     </button>
                  </div>

                  {/* Scoreboard Switch */}
                  <div className="flex items-center justify-between p-3 bg-black/20 rounded-lg border border-[#8E9285]/10">
                     <div className="flex flex-col">
                        <span className="text-[#E2E3D8] font-medium">Scoreboard style</span>
                        <span className="text-[#8E9285] text-xs">{scoreboard ? "TV Broadcast" : "Server-First"}</span>
                     </div>
                     <button onClick={() => setScoreboard(!scoreboard)} className={`w-12 h-6 rounded-full transition-colors duration-200 ease-in-out relative ${scoreboard ? 'bg-[#B0D18B]' : 'bg-[#2C2E26]'}`}>
                        <span className={`absolute top-1 left-1 bg-white w-4 h-4 rounded-full shadow-sm transition-transform duration-200 ease-in-out ${scoreboard ? 'translate-x-6' : 'translate-x-0'}`} />
                     </button>
                  </div>

                  {/* Save Settings Button */}
                  <div className="flex justify-center mt-4">
                    <button onClick={handleSaveSettings} disabled={savingSettings} className="w-full sm:w-1/4 bg-[#B0D18B] hover:bg-[#9DC077] text-[#1A1C16] font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed">
                       {savingSettings ? <Loader2 className="h-5 w-5 animate-spin" /> : <>
                             <Save className="h-5 w-5" />
                             Save Settings
                          </>}
                    </button>
                  </div>
               </div>
            </div>

          </div>
        </section>
      </div>;
  }

  // --- AUTH FORMS ---
  return <div className="animate-in fade-in duration-500">
      <section className="max-w-md mx-auto px-4 pt-12 pb-24">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-sriracha tracking-tighter text-[#B0D18B] mb-2">
            {view === 'login' && 'Welcome Back'}
            {view === 'signup' && 'Create Account'}
            {view === 'forgot-password' && 'Reset Password'}
          </h1>
          <p className="text-[#C4C8BA] text-sm">
            {view === 'login' && 'Sign in to access your account'}
            {view === 'signup' && 'Join the MVPlayer community'}
            {view === 'forgot-password' && 'Enter your email to receive reset instructions'}
          </p>
        </div>

        <div className="bg-[#1A1C16] border border-[#8E9285]/20 rounded-2xl p-6 sm:p-8 shadow-xl">
          
          {error && <div className="mb-6 p-3 bg-red-500/10 border border-red-500/20 rounded-lg flex items-start gap-3 text-red-400 text-sm">
              <AlertCircle className="h-5 w-5 shrink-0" />
              <p>{error}</p>
            </div>}

          {message && <div className="mb-6 p-3 bg-green-500/10 border border-green-500/20 rounded-lg flex items-start gap-3 text-green-400 text-sm">
              <CheckCircle className="h-5 w-5 shrink-0" />
              <p>{message}</p>
            </div>}

          <form onSubmit={view === 'login' ? handleLogin : view === 'signup' ? handleSignup : handleResetPassword} className="space-y-4">
            
            <div className="space-y-1">
              <label className="text-[#8E9285] text-xs uppercase font-semibold tracking-wider ml-1">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#8E9285]" />
                <input type="email" required value={email} onChange={e_4 => setEmail(e_4.target.value)} className="w-full bg-black/20 border border-[#8E9285]/20 rounded-lg py-3 pl-10 pr-4 text-[#E2E3D8] placeholder-[#5C5F56] focus:outline-none focus:border-[#B0D18B] transition-colors" placeholder="you@example.com" />
              </div>
            </div>

            {view !== 'forgot-password' && <div className="space-y-1">
                <label className="text-[#8E9285] text-xs uppercase font-semibold tracking-wider ml-1">Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-[#8E9285]" />
                  <input type="password" required value={password} onChange={e_5 => setPassword(e_5.target.value)} className="w-full bg-black/20 border border-[#8E9285]/20 rounded-lg py-3 pl-10 pr-4 text-[#E2E3D8] placeholder-[#5C5F56] focus:outline-none focus:border-[#B0D18B] transition-colors" placeholder="••••••••" />
                </div>
              </div>}

            <div className="flex justify-center">
              <button type="submit" disabled={isSubmitting} className="w-full sm:w-1/4 bg-[#B0D18B] hover:bg-[#9DC077] text-[#1A1C16] font-bold py-3 rounded-lg transition-colors flex items-center justify-center gap-2 mt-2 disabled:opacity-50 disabled:cursor-not-allowed">
                {isSubmitting ? <Loader2 className="h-5 w-5 animate-spin" /> : <>
                    {view === 'login' && 'Sign In'}
                    {view === 'signup' && 'Create Account'}
                    {view === 'forgot-password' && 'Send Reset Link'}
                  </>}
              </button>
            </div>
          </form>

          {view === 'login' && <>
              <div className="relative my-6">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-[#8E9285]/20"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-[#1A1C16] text-[#8E9285]">Or continue with</span>
                </div>
              </div>

              <div className="flex justify-center">
                <button type="button" onClick={handleGoogleLogin} disabled={isSubmitting} className="w-full sm:w-1/4 bg-[#2C2E26] hover:bg-[#3C3E36] text-[#E2E3D8] font-medium py-3 rounded-lg transition-colors border border-[#8E9285]/20 flex items-center justify-center gap-3 disabled:opacity-50">
                  <img src="/drawables/google-favicon-logo-brandlogos.net_gvd970bt9.svg" alt="Google" className="w-5 h-5" />
                  Continue with Google
                </button>
              </div>
            </>}

          <div className="mt-8 pt-6 border-t border-[#8E9285]/20 text-center space-y-2">
             {view === 'login' && <>
                  <p className="text-[#8E9285] text-sm">
                    Don't have an account?{' '}
                    <button onClick={() => {
                clearState();
                setView('signup');
              }} className="text-[#B0D18B] hover:underline font-semibold">
                      Sign up
                    </button>
                  </p>
                  <p className="text-[#8E9285] text-sm">
                    <button onClick={() => {
                clearState();
                setView('forgot-password');
              }} className="text-[#8E9285] hover:text-[#B0D18B] hover:underline transition-colors">
                      Forgot your password?
                    </button>
                  </p>
                </>}

             {view === 'signup' && <p className="text-[#8E9285] text-sm">
                  Already have an account?{' '}
                  <button onClick={() => {
              clearState();
              setView('login');
            }} className="text-[#B0D18B] hover:underline font-semibold">
                    Sign in
                  </button>
                </p>}

             {view === 'forgot-password' && <p className="text-[#8E9285] text-sm">
                  Remember your password?{' '}
                  <button onClick={() => {
              clearState();
              setView('login');
            }} className="text-[#B0D18B] hover:underline font-semibold">
                    Back to login
                  </button>
                </p>}
          </div>

        </div>
      </section>
    </div>;
}
export default function AccountPage() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <Suspense fallback={<div className="min-h-[50vh] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-[#B0D18B]" /></div>}><AccountContent /></Suspense>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}